import { useEffect, useRef } from 'react';
import { useCamera } from '../hooks/useCamera';
import { Hole, Rectangle } from '../types';

interface CameraViewProps {
  holes: Hole[];
  startZone?: Rectangle;
  gutterZone?: Rectangle;
  onCanvasReady?: (canvas: HTMLCanvasElement, video: HTMLVideoElement) => void;
  showDetection?: boolean;
}

export function CameraView({ holes, startZone, gutterZone, onCanvasReady, showDetection = true }: CameraViewProps) {
  const { videoRef, error, isReady } = useCamera();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    if (!isReady || !videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Notify parent that canvas is ready
    if (onCanvasReady) {
      onCanvasReady(canvas, video);
    }

    // Animation loop to draw overlay
    function drawOverlay() {
      if (!ctx || !video || !canvas) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw start zone (green rectangle)
      if (startZone) {
        ctx.strokeStyle = '#4CAF50';
        ctx.lineWidth = 3;
        ctx.strokeRect(startZone.x, startZone.y, startZone.width, startZone.height);
        ctx.fillStyle = 'rgba(76, 175, 80, 0.1)';
        ctx.fillRect(startZone.x, startZone.y, startZone.width, startZone.height);

        // Label
        ctx.fillStyle = '#4CAF50';
        ctx.font = 'bold 20px Arial';
        ctx.fillText('START', startZone.x + 10, startZone.y + 25);
      }

      // Draw gutter zone (red rectangle)
      if (gutterZone) {
        ctx.strokeStyle = '#f44336';
        ctx.lineWidth = 3;
        ctx.strokeRect(gutterZone.x, gutterZone.y, gutterZone.width, gutterZone.height);
        ctx.fillStyle = 'rgba(244, 67, 54, 0.1)';
        ctx.fillRect(gutterZone.x, gutterZone.y, gutterZone.width, gutterZone.height);

        // Label
        ctx.fillStyle = '#f44336';
        ctx.font = 'bold 20px Arial';
        ctx.fillText('GUTTER (0pt)', gutterZone.x + 10, gutterZone.y + 25);
      }

      // Draw holes
      holes.forEach(hole => {
        const shape = hole.shape || 'circle';
        ctx.strokeStyle = getHoleColor(hole.value);
        ctx.lineWidth = 3;

        if (shape === 'rectangle' && hole.width !== undefined && hole.height !== undefined) {
          // Draw rectangle hole
          ctx.strokeRect(hole.x, hole.y, hole.width, hole.height);

          // Draw hole value label at center of rectangle
          ctx.fillStyle = getHoleColor(hole.value);
          ctx.font = 'bold 24px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const centerX = hole.x + hole.width / 2;
          const centerY = hole.y + hole.height / 2;
          ctx.fillText(hole.value.toString(), centerX, centerY);
        } else {
          // Draw circle hole
          ctx.beginPath();
          ctx.arc(hole.x, hole.y, hole.radius || 0, 0, 2 * Math.PI);
          ctx.stroke();

          // Draw hole value label at center
          ctx.fillStyle = getHoleColor(hole.value);
          ctx.font = 'bold 24px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(hole.value.toString(), hole.x, hole.y);
        }
      });

      if (showDetection) {
        animationFrameRef.current = requestAnimationFrame(drawOverlay);
      }
    }

    drawOverlay();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isReady, holes, startZone, gutterZone, onCanvasReady, showDetection]);

  if (error) {
    return (
      <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>
        <h3>Camera Error</h3>
        <p>{error}</p>
        <p>Please ensure camera permissions are granted.</p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '1280px', margin: '0 auto' }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          backgroundColor: '#000',
        }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      />
      {!isReady && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'white',
            fontSize: '18px',
            textAlign: 'center',
          }}
        >
          Loading camera...
        </div>
      )}
    </div>
  );
}

function getHoleColor(value: number): string {
  switch (value) {
    case 0:
      return '#888888'; // Gray for gutter
    case 10:
      return '#FF6B6B'; // Red
    case 20:
      return '#FFA500'; // Orange
    case 30:
      return '#FFD700'; // Gold
    case 40:
      return '#4ECDC4'; // Teal
    case 50:
      return '#45B7D1'; // Blue
    case 100:
      return '#96CEB4'; // Green
    default:
      return '#FFFFFF'; // White
  }
}

